import React from "react";
import Parse from "parse";
import { observer } from "mobx-react-lite";
import { Card } from "antd";
import { Typography, Box, Link } from "@material-ui/core";

type Props = {};

export const PrivacyPolicyGameRoute = observer((props: Props) => {
  return (
    <Card>
      <Typography variant="h4" gutterBottom>
        Informationen zum Datenschutz der Spieldurchführung im Forschungsprojekt
        AntiScam
      </Typography>

      <Typography variant="body1" paragraph>
        Wir bedanken uns für Ihre Bereitschaft, das Forschungsprojekt „AntiScam"
        (folgend: Projekt) bei der empirischen Arbeit zu unterstützen. Der
        nachfolgende Text wurde für eine einfachere Lesbarkeit im generischen
        Maskulinum verfasst. Selbstverständlich werden alle Menschen jeglicher
        geschlechterspezifischer Identifizierung angesprochen.
      </Typography>

      <Typography variant="h5" gutterBottom>
        I. Projektinhalt und -ziel
      </Typography>

      <Typography variant="body1" paragraph>
        Das Projekt zielt darauf ab, Verbraucher vor den wachsenden Gefahren
        durch konversationellen Betrug zu schützen. Es verfolgt einen
        ganzheitlichen, sozio-technischen Ansatz mit vier Kernzielen:
      </Typography>

      <Box component="ol" sx={{ pl: 4 }}>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Bedrohungsanalyse:
          </Box>{" "}
          Entwicklung einer Taxonomie und Bedrohungskarte für KI-basierte
          Gesprächsbetrugsformen inklusive möglicher Abwehrmaßnahmen.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Erklärbare Betrugserkennung:
          </Box>{" "}
          Einsatz erklärbarer KI (XAI) zur Erkennung und Nachvollziehbarkeit von
          Angriffen mit synthetischen Stimmen.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Verbraucherbildung:
          </Box>{" "}
          Gestaltung eines Lernspiels zur Sensibilisierung und Stärkung der
          Selbstschutzkompetenz gegenüber Betrug.
        </Box>
        <Box component="li" mb={1}>
          <Box component="span" fontWeight="bold">
            Rechtliche Analyse:
          </Box>{" "}
          Untersuchung rechtlicher Lücken im Umgang mit KI-gestütztem
          Online-Betrug und Ableitung von Handlungsempfehlungen.
        </Box>
      </Box>

      <Typography variant="body1" paragraph>
        Insgesamt ergänzen sich die Teilziele gegenseitig. Die Bedrohungskarte
        liefert die Grundlage für die Entwicklung sowohl technischer
        Abwehrmaßnahmen als auch von Strategien zur Selbstverteidigung. Die
        technischen Schutzmechanismen bauen auf diesen Erkenntnissen auf, um
        wirksame und nachvollziehbare Lösungen für Verbraucher bereitzustellen.
        Die spielbasierte Verbraucherbildung vermittelt Selbstschutzstrategien,
        die sowohl auf der Bedrohungskarte als auch auf technischen
        Erkenntnissen beruhen, um Nutzer zu befähigen, sich aktiv gegen
        KI-basierte Angriffe zu schützen. In allen Bereichen werden zudem die
        rechtlichen Fragestellungen beleuchtet, die sich aus den neuen
        Missbrauchspotenzialen von KI-Systemen ergeben.
      </Typography>

      <Typography variant="h5" gutterBottom>
        II. Informationen über die Verarbeitung Ihrer Daten gemäß Art. 13 der
        Datenschutz-Grundverordnung (DSGVO)
      </Typography>

      <Typography variant="h6" gutterBottom>
        a. Welche Daten werden für welche Zwecke verarbeitet?
      </Typography>

      <Typography variant="body1" paragraph>
        Bei jedem Zugriff auf Inhalte der Website werden vorübergehend Daten
        gespeichert, die möglicherweise eine Identifizierung zulassen. Die
        folgenden Daten werden hierbei erhoben:
      </Typography>

      <Box component="ul" sx={{ pl: 4 }}>
        <Box component="li">Datum und Uhrzeit des Zugriffs</Box>
        <Box component="li">Meldung, wenn der Abruf nicht erfolgreich war</Box>
        <Box component="li">Benutzerauthentifizierung (Logindaten)</Box>
      </Box>

      <Typography variant="body1" paragraph>
        Die vorübergehende Speicherung der Daten ist für den Ablauf eines
        Websitebesuchs erforderlich, um eine Auslieferung der Website zu
        ermöglichen. Eine weitere Speicherung in Protokolldateien erfolgt, um
        die Funktionsfähigkeit der Website und die Sicherheit der
        informationstechnischen Systeme sicherzustellen. In diesen Zwecken liegt
        auch unser berechtigtes Interesse an der Datenverarbeitung.
      </Typography>

      <Typography variant="h6" gutterBottom>
        b. Auf welcher Rechtsgrundlage werden diese Daten verarbeitet?
      </Typography>

      <Typography variant="body1" paragraph>
        Die Daten werden auf der Grundlage des Art. 6 Abs. 1 lit. f) DSGVO
        verarbeitet.
      </Typography>

      <Typography variant="h6" gutterBottom>
        c. Wie lange werden die Daten gespeichert?
      </Typography>

      <Typography variant="body1" paragraph>
        Die Daten werden gelöscht, sobald sie für die Erreichung des Zwecks
        ihrer Erhebung nicht mehr erforderlich sind.
      </Typography>

      <Typography variant="h5" gutterBottom>
        III. Betroffenenrechte
      </Typography>

      <Typography variant="h6" gutterBottom>
        a. Recht auf Auskunft Art. 15 DSGVO
      </Typography>

      <Typography variant="body1" paragraph>
        Die Betroffenen haben das Recht, jederzeit Auskunft über die zu ihrer
        Person verarbeiteten Daten sowie die möglichen Empfänger dieser Daten
        verlangen zu können. Ihnen steht eine Antwort innerhalb einer Frist von
        einem Monat nach Eingang des Auskunftsersuchens zu.
      </Typography>

      <Typography variant="h6" gutterBottom>
        b. Recht auf Berichtigung, Löschung und Einschränkung (Art. 16-18 DSGVO)
      </Typography>

      <Typography variant="body1" paragraph>
        Die Betroffenen können jederzeit gegenüber den Projektverantwortlichen
        die Berichtigung, Löschung ihrer personenbezogenen Daten bzw. die
        Einschränkung der Verarbeitung zu verlangen.
      </Typography>

      <Typography variant="h6" gutterBottom>
        c. Recht auf Datenübertragbarkeit
      </Typography>

      <Typography variant="body1" paragraph>
        Für den Fall, dass die Voraussetzungen des Art. 20 Abs. 1 DSGVO
        vorliegen, steht Ihnen das Recht zu, sich Daten, die wir auf Grundlage
        Ihrer Einwilligung oder in Erfüllung eines Vertrages automatisiert
        verarbeiten, an sich oder an Dritte aushändigen zu lassen. Die Erfassung
        der Daten zur Bereitstellung der Website und die Speicherung der
        Protokolldateien sind für den Betreiber der Internetseite zwingend
        erforderlich. Sie beruhen daher nicht auf einer Einwilligung nach Art. 6
        Abs. 1 lit. a) DSGVO oder auf einem Vertrag nach Art. 6 Abs. 1 lit. b)
        DSGVO, sondern sind nach Art. 6 Abs. 1 lit. f) DSGVO gerechtfertigt. Die
        Voraussetzungen des Art. 20 Abs. 1 DSGVO sind demnach insoweit nicht
        erfüllt.
      </Typography>

      <Typography variant="h6" gutterBottom>
        d. Recht auf Widerspruch gemäß Art. 21 Abs. 1 DSGVO
      </Typography>

      <Typography variant="body1" paragraph>
        Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen
        Situation ergeben, jederzeit gegen die Verarbeitung Ihrer
        personenbezogenen Daten, die aufgrund von Art. 6 Abs. 1 lit. f) DSGVO
        erfolgt, Widerspruch einzulegen. Der Verantwortliche verarbeitet die
        personenbezogenen Daten dann nicht mehr, es sei denn, er kann zwingend
        schutzwürdige Gründe für die Verarbeitung nachweisen, die die
        Interessen, Rechte und Freiheiten der betroffenen Person überwiegen,
        oder die Verarbeitung dient der Geltendmachung, Ausübung oder
        Verteidigung von Rechtsansprüchen. Die Erfassung der Daten zur
        Bereitstellung der Website und die Speicherung der Protokolldateien sind
        für den Betrieb der Internetseite zwingend erforderlich.
      </Typography>

      <Typography variant="h6" gutterBottom>
        e. Recht auf Beschwerde
      </Typography>

      <Typography variant="body1" paragraph>
        Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen
        Daten gegen Datenschutzrecht verstößt, haben Sie nach Art. 77 Abs. 1
        DSGVO das Recht, sich bei einer Datenschutzaufsichtsbehörde eigener Wahl
        zu beschweren. Hierzu gehört auch die für den Verantwortlichen
        zuständige Datenschutzaufsichtsbehörde: Landesbeauftragte für
        Datenschutz und Informationsfreiheit Nordrhein-Westfalen,{" "}
        <Link
          href="https://www.ldi.nrw.de/kontakt/ihre-beschwerde"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.ldi.nrw.de/kontakt/ihre-beschwerde
        </Link>
        .
      </Typography>

      <Typography variant="body1" paragraph>
        Beachten Sie zudem, dass aufgrund des Forschungscharakters des Projekts
        die in Art. 15, 16, 18 und 21 DSGVO vorgesehenen Rechte insoweit
        beschränkt sind, als diese Rechte voraussichtlich die Verwirklichung des
        Forschungszwecks unmöglich macht oder ernsthaft beeinträchtigt und die
        Beschränkung für die Erfüllung der Forschungs- oder Statistikzwecke
        notwendig ist. Das Recht auf Auskunft gemäß Art. 15 DSGVO besteht
        darüber hinaus nicht, wenn die Daten für Zwecke der wissenschaftlichen
        Forschung erforderlich sind und die Auskunftserteilung einen
        unverhältnismäßigen Aufwand erfordern würde (§ 28 Abs. 2 BDSG).
      </Typography>

      <Typography variant="h5" gutterBottom>
        IV. Kontaktdaten
      </Typography>

      <Typography variant="body1" paragraph>
        Bei Fragen zum Projekt wenden Sie sich bitte an Prof. Dr. Gunnar Stevens
        (gunnar.stevens@uni-siegen.de). Bei Fragen zum Datenschutz wenden Sie
        sich bitte an Frau Charlene Pfeiffer (pfeiffer@recht.uni-siegen.de).
      </Typography>
    </Card>
  );
});
